import PercentIcon from '@mui/icons-material/Percent';
import { Box, InputAdornment, Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import sortBy from 'lodash-es/sortBy';
import React, { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import TARGETING_TACTICS from '../../../constants/adGroupTargeting';
import { useConstants } from '../../../hooks/useConstants';
import { convertHourOfWeekToJsDate, convertJsDateToHourOfWeek } from '../../../util/helpers';
import AutocompleteArray from '../../forms/AutocompleteArray';
import CheckboxArray from '../../forms/CheckboxArray';
import NumericTextField from '../../forms/NumericTextField';
import RadioGroup from '../../forms/RadioGroup';
import ScheduleSelector from '../../forms/ScheduleSelector';
import Select from '../../forms/Select';
import Switch from '../../forms/Switch';
import TextField from '../../forms/TextField';
import TransientField from '../../forms/TransientField';
import CollapsiblePaper from '../../shared/CollapsiblePaper';
import TacticsPicker from './TacticsPicker';
import TacticsSearch from './TacticsSearch';

// Copied the way the input/output of textfields in Ventura is normalized
const normalizeTextFieldOutput = (e) => e.target.value.split(/,/g).map((x) => x.trim()) || [];
const normalizeTextFieldInput = (input) => input.join(',');

function FieldContainer({ label, children }: React.PropsWithChildren<{ label: string }>) {
  return (
    <Box sx={{ paddingBottom: 2 }}>
      <Typography sx={{ paddingBottom: 1 }}>{label}</Typography>
      {children}
    </Box>
  );
}

function TargetingTactics() {
  const { watch } = useFormContext();
  // selectedTactics only works with useWatch (and not watch); might be because it's used with useMemo?
  const selectedTactics = useWatch({ name: 'targetingsEnabled' });
  // mediaType and targetStrategy only works with `watch`.
  const mediaType = watch('mediaType');
  const targetStrategy = watch('targetStrategy');
  const { constants } = useConstants();

  const ACCEPTED_BRANDSAFE_SCORES = get(constants, 'CAMPAIGN.ACCEPTED_BRANDSAFE_SCORES', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_WHITELIST_TIERS = get(constants, 'CAMPAIGN.ACCEPTED_WHITELIST_TIERS', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_VCR_WHITELIST_TIERS = get(constants, 'CAMPAIGN.ACCEPTED_VCR_WHITELIST_TIERS', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_CATEGORIES = get(constants, 'CATEGORY.ACCEPTED_CATEGORIES', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_DEVICES = get(constants, 'CAMPAIGN.ACCEPTED_DEVICES', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const APP_OR_SITE = get(constants, 'CAMPAIGN.APP_OR_SITE', []).map((item) => ({ value: item[0], label: item[1] }));
  const ACCEPTED_CARRIERS = get(constants, 'CAMPAIGN.ACCEPTED_CARRIERS', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_GENDERS = get(constants, 'CAMPAIGN.ACCEPTED_GENDERS', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_EXCHANGES = get(constants, 'CAMPAIGN.ACCEPTED_EXCHANGES', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_SKIPPABILITY_OPTIONS = get(constants, 'CAMPAIGN.ACCEPTED_SKIPPABILITY_OPTIONS', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_RESTRICTED_TARGETINGS = get(constants, 'CAMPAIGN.ACCEPTED_RESTRICTED_TARGETINGS', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_VIDEO_AD_POSITIONS = get(constants, 'CAMPAIGN.ACCEPTED_VIDEO_AD_POSITIONS', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_REGION_STATES = get(constants, 'CAMPAIGN.ACCEPTED_REGION_STATES', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const ACCEPTED_REGION_DMAS = get(constants, 'CAMPAIGN.ACCEPTED_REGION_DMAS', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));

  const filterByMediaType = useCallback(
    (tactic) => {
      let returnValue = true;
      if (tactic === 'app_whitelist_tier') {
        returnValue = mediaType !== 'video';
      }
      if (tactic === 'app_blacklist_threshold') {
        returnValue = mediaType !== 'video';
      }
      if (tactic === 'app_vcr_whitelist_tier') {
        returnValue = mediaType === 'video';
      }
      if (tactic === 'app_vcr_blacklist_threshold') {
        returnValue = mediaType === 'video';
      }
      if (tactic === 'WHITELISTED_STATE_IDS') {
        returnValue = targetStrategy === 'geocookie';
      }
      if (tactic === 'WHITELISTED_NIELSEN_DMAS') {
        returnValue = targetStrategy === 'geocookie';
      }
      // Device is already hard coded in the UI. Do not included in the number of selected tactics.
      if (tactic === 'DEVICE_COUNTRIES') {
        returnValue = false;
      }
      return returnValue;
    },
    [mediaType, targetStrategy]
  );

  const sortTactics = (key) => TARGETING_TACTICS[key]?.label;

  const sortedUnselectedTacticsByLabel = useMemo(
    () =>
      sortBy(
        Object.keys(TARGETING_TACTICS)
          .filter((key) => TARGETING_TACTICS[key] !== false && !selectedTactics[key])
          .filter(filterByMediaType),
        sortTactics
      ),
    [selectedTactics, filterByMediaType]
  );

  const sortedUnselectedTacticsByExclusion = sortedUnselectedTacticsByLabel.filter(
    (key) => TARGETING_TACTICS[key].label.toLowerCase().indexOf('exclusion') >= 0
  );
  const sortedUnselectedTacticsByInclusion = sortedUnselectedTacticsByLabel.filter(
    (key) => TARGETING_TACTICS[key].label.toLowerCase().indexOf('inclusion') >= 0
  );
  const sortedUnselectedTacticsByTargeting = sortedUnselectedTacticsByLabel.filter(
    (key) => !sortedUnselectedTacticsByExclusion.includes(key) && !sortedUnselectedTacticsByInclusion.includes(key)
  );

  const sortedSelectedTacticsByLabel = useMemo(
    () =>
      sortBy(
        Object.keys(selectedTactics)
          .filter(filterByMediaType)
          .filter((key) => selectedTactics[key]),
        sortTactics
      ),
    [selectedTactics, filterByMediaType]
  );

  return (
    <CollapsiblePaper
      sessionKey="targeting-tactics"
      title={`Tactics (${sortedSelectedTacticsByLabel.length})`}
      summary={<TacticsPicker tactics={sortedSelectedTacticsByLabel} />}
    >
      <Stack spacing={2}>
        <TacticsSearch tactics={sortedUnselectedTacticsByLabel} />
        <Typography>Exclusion</Typography>
        <TacticsPicker tactics={sortedUnselectedTacticsByExclusion} />
        <Typography>Inclusion</Typography>
        <TacticsPicker tactics={sortedUnselectedTacticsByInclusion} />
        <Typography>Targeting</Typography>
        <TacticsPicker tactics={sortedUnselectedTacticsByTargeting} />
        {sortedSelectedTacticsByLabel.map((selectedTacticKey) => {
          if (selectedTacticKey === TARGETING_TACTICS.BRANDSAFE_SCORES.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.BRANDSAFE_SCORES.label}>
                <TransientField>
                  <CheckboxArray
                    row
                    name={`targetingProfile.${TARGETING_TACTICS.BRANDSAFE_SCORES.key.toLowerCase()}`}
                    options={ACCEPTED_BRANDSAFE_SCORES}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.LOCATION_SCORES.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.LOCATION_SCORES.label}>
                <TransientField>
                  <NumericTextField
                    name="targetingProfile.min_location_score"
                    label="Min"
                    placeholder="0"
                    allowNegative={false}
                    decimalScale={0}
                    sx={{ marginRight: { sm: 2 }, marginBottom: { xs: 2, lg: 0 } }}
                  />
                </TransientField>
                <TransientField>
                  <NumericTextField
                    name="targetingProfile.max_location_score"
                    label="Max"
                    placeholder="100"
                    allowNegative={false}
                    decimalScale={0}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.app_blacklist_threshold.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.app_blacklist_threshold.label}>
                <TransientField>
                  <NumericTextField
                    name={`targetingProfile.${TARGETING_TACTICS.app_blacklist_threshold.key.toLowerCase()}`}
                    label=""
                    placeholder="40"
                    allowNegative={false}
                    decimalScale={0}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PercentIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: { xs: '100%', sm: 200 } }}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.app_whitelist_tier.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.app_whitelist_tier.label}>
                <TransientField>
                  <Select
                    label=""
                    name={`targetingProfile.${TARGETING_TACTICS.app_whitelist_tier.key.toLowerCase()}`}
                    placeholder="CTR Tier"
                    options={ACCEPTED_WHITELIST_TIERS}
                    formControlProps={{ sx: { minWidth: { xs: '100%', sm: 200 } } }}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.app_vcr_blacklist_threshold.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.app_vcr_blacklist_threshold.label}>
                <TransientField>
                  <NumericTextField
                    name={`targetingProfile.${TARGETING_TACTICS.app_vcr_blacklist_threshold.key.toLowerCase()}`}
                    label="Threshold (‱) Indefinitely"
                    placeholder="40"
                    allowNegative={false}
                    decimalScale={0}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PercentIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: { xs: '100%', sm: 200 } }}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.app_vcr_whitelist_tier.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.app_vcr_whitelist_tier.label}>
                <TransientField>
                  <Select
                    name={`targetingProfile.${TARGETING_TACTICS.app_vcr_whitelist_tier.key.toLowerCase()}`}
                    label="VCR Tier"
                    placeholder="VCR Tier"
                    options={ACCEPTED_VCR_WHITELIST_TIERS}
                    formControlProps={{ sx: { minWidth: { xs: '100%', sm: 200 } } }}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.BLACKLISTED_APP_AD_SPACE_IDS.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.BLACKLISTED_APP_AD_SPACE_IDS.label}>
                <TransientField>
                  <TextField
                    label=""
                    key={TARGETING_TACTICS.BLACKLISTED_APP_AD_SPACE_IDS.key}
                    name={`targetingProfile.${TARGETING_TACTICS.BLACKLISTED_APP_AD_SPACE_IDS.key.toLowerCase()}`}
                    multiline
                    maxRows={8}
                    sx={{ width: { xs: '100%' } }}
                    placeholder="App Ad Space ID"
                    normalizeOutput={normalizeTextFieldOutput}
                    normalizeInput={normalizeTextFieldInput}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.WHITELISTED_APP_AD_SPACE_IDS.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.WHITELISTED_APP_AD_SPACE_IDS.label}>
                <TransientField>
                  <TextField
                    label=""
                    key={TARGETING_TACTICS.WHITELISTED_APP_AD_SPACE_IDS.key}
                    name={`targetingProfile.${TARGETING_TACTICS.WHITELISTED_APP_AD_SPACE_IDS.key.toLowerCase()}`}
                    multiline
                    maxRows={8}
                    sx={{ width: { xs: '100%' } }}
                    placeholder="Bundle ID"
                    normalizeOutput={normalizeTextFieldOutput}
                    normalizeInput={normalizeTextFieldInput}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.BLACKLISTED_BUNDLE_IDS.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.BLACKLISTED_BUNDLE_IDS.label}>
                <TransientField>
                  <TextField
                    label=""
                    key={TARGETING_TACTICS.BLACKLISTED_BUNDLE_IDS.key}
                    name={`targetingProfile.${TARGETING_TACTICS.BLACKLISTED_BUNDLE_IDS.key.toLowerCase()}`}
                    multiline
                    maxRows={8}
                    sx={{ width: { xs: '100%' } }}
                    placeholder="Bundle ID"
                    normalizeOutput={normalizeTextFieldOutput}
                    normalizeInput={normalizeTextFieldInput}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.WHITELISTED_BUNDLE_IDS.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.WHITELISTED_BUNDLE_IDS.label}>
                <TransientField>
                  <TextField
                    label=""
                    key={TARGETING_TACTICS.WHITELISTED_BUNDLE_IDS.key}
                    name={`targetingProfile.${TARGETING_TACTICS.WHITELISTED_BUNDLE_IDS.key.toLowerCase()}`}
                    multiline
                    maxRows={8}
                    sx={{ width: { xs: '100%' } }}
                    placeholder="App Ad Space ID"
                    normalizeOutput={normalizeTextFieldOutput}
                    normalizeInput={normalizeTextFieldInput}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.WHITELISTED_CATEGORIES.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.WHITELISTED_CATEGORIES.label}>
                <TransientField>
                  <CheckboxArray
                    row
                    name={`targetingProfile.${TARGETING_TACTICS.WHITELISTED_CATEGORIES.key.toLowerCase()}`}
                    options={ACCEPTED_CATEGORIES}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.BLACKLISTED_CATEGORIES.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.BLACKLISTED_CATEGORIES.label}>
                <TransientField>
                  <CheckboxArray
                    row
                    name={`targetingProfile.${TARGETING_TACTICS.BLACKLISTED_CATEGORIES.key.toLowerCase()}`}
                    options={ACCEPTED_CATEGORIES}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.HOURS.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.HOURS.label}>
                <TransientField>
                  <ScheduleSelector
                    name={`targetingProfile.${TARGETING_TACTICS.HOURS.key.toLowerCase()}`}
                    normalizeInput={convertHourOfWeekToJsDate}
                    normalizeOutput={convertJsDateToHourOfWeek}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.min_conversion_rate.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.min_conversion_rate.label}>
                <TransientField>
                  <NumericTextField
                    name={`targetingProfile.${TARGETING_TACTICS.min_conversion_rate.key.toLowerCase()}`}
                    label=""
                    placeholder="0.0"
                    allowNegative={false}
                    decimalScale={5}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PercentIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: { xs: '100%', sm: 200 } }}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.conversion_rate_exclusion.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.conversion_rate_exclusion.label}>
                <TransientField>
                  <NumericTextField
                    name={`targetingProfile.${TARGETING_TACTICS.conversion_rate_exclusion.key.toLowerCase()}`}
                    label=""
                    placeholder="0.0"
                    allowNegative={false}
                    decimalScale={5}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PercentIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: { xs: '100%', sm: 200 } }}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.WHITELISTED_DEVICES.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.WHITELISTED_DEVICES.label}>
                <TransientField>
                  <CheckboxArray
                    row
                    name={`targetingProfile.${TARGETING_TACTICS.WHITELISTED_DEVICES.key.toLowerCase()}`}
                    options={ACCEPTED_DEVICES}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.BLACKLISTED_DEVICES.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.BLACKLISTED_DEVICES.label}>
                <TransientField>
                  <CheckboxArray
                    row
                    name={`targetingProfile.${TARGETING_TACTICS.BLACKLISTED_DEVICES.key.toLowerCase()}`}
                    options={ACCEPTED_DEVICES}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.APP_OR_SITE.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.APP_OR_SITE.label}>
                <TransientField>
                  <RadioGroup
                    name={`targetingProfile.${TARGETING_TACTICS.APP_OR_SITE.key.toLowerCase()}`}
                    options={APP_OR_SITE}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.INCLUDED_APP_BUNDLES.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.INCLUDED_APP_BUNDLES.label}>
                <TransientField>
                  <TextField
                    label=""
                    key={TARGETING_TACTICS.INCLUDED_APP_BUNDLES.key}
                    name={`targetingProfile.${TARGETING_TACTICS.INCLUDED_APP_BUNDLES.key.toLowerCase()}`}
                    multiline
                    maxRows={8}
                    normalizeOutput={normalizeTextFieldOutput}
                    normalizeInput={normalizeTextFieldInput}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.EXCLUDED_APP_BUNDLES.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.EXCLUDED_APP_BUNDLES.label}>
                <TransientField>
                  <TextField
                    label=""
                    key={TARGETING_TACTICS.EXCLUDED_APP_BUNDLES.key}
                    name={`targetingProfile.${TARGETING_TACTICS.EXCLUDED_APP_BUNDLES.key.toLowerCase()}`}
                    multiline
                    maxRows={8}
                    sx={{ width: { xs: '100%' } }}
                    normalizeOutput={normalizeTextFieldOutput}
                    normalizeInput={normalizeTextFieldInput}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.WHITELISTED_CARRIERS.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.WHITELISTED_CARRIERS.label}>
                <TransientField>
                  <CheckboxArray
                    row
                    name={`targetingProfile.${TARGETING_TACTICS.WHITELISTED_CARRIERS.key.toLowerCase()}`}
                    options={ACCEPTED_CARRIERS}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.BLACKLISTED_CARRIERS.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.BLACKLISTED_CARRIERS.label}>
                <TransientField>
                  <CheckboxArray
                    row
                    name={`targetingProfile.${TARGETING_TACTICS.BLACKLISTED_CARRIERS.key.toLowerCase()}`}
                    options={ACCEPTED_CARRIERS}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.AGE_TARGETING.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.AGE_TARGETING.label}>
                <TransientField>
                  <NumericTextField
                    name="targetingProfile.min_age"
                    label="Min"
                    placeholder="0"
                    allowNegative={false}
                    decimalScale={0}
                    sx={{ marginRight: { sm: 2 }, marginBottom: { xs: 2, lg: 0 } }}
                  />
                </TransientField>
                <TransientField>
                  <NumericTextField
                    name="targetingProfile.max_age"
                    label="Max"
                    placeholder="100"
                    allowNegative={false}
                    decimalScale={0}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.WHITELISTED_GENDERS.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.WHITELISTED_GENDERS.label}>
                <TransientField>
                  <CheckboxArray
                    row
                    name={`targetingProfile.${TARGETING_TACTICS.WHITELISTED_GENDERS.key.toLowerCase()}`}
                    options={ACCEPTED_GENDERS}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.location_fetch_enabled.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.location_fetch_enabled.label}>
                <TransientField>
                  <Switch
                    name={`targetingProfile.${TARGETING_TACTICS.location_fetch_enabled.key.toLowerCase()}`}
                    label="Enabled"
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.needs_tn_user_id.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.needs_tn_user_id.label}>
                <TransientField>
                  <Switch
                    name={`targetingProfile.${TARGETING_TACTICS.needs_tn_user_id.key.toLowerCase()}`}
                    label="Enabled"
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.LOCATION_SCORES.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.LOCATION_SCORES.label}>
                <TransientField>
                  <NumericTextField
                    name="targetingProfile.min_location_score"
                    label="Min"
                    placeholder="0"
                    allowNegative={false}
                    decimalScale={0}
                    sx={{ marginRight: { sm: 2 }, marginBottom: { xs: 2, lg: 0 } }}
                  />
                </TransientField>
                <TransientField>
                  <NumericTextField
                    name="targetingProfile.max_location_score"
                    label="Max"
                    placeholder="99"
                    allowNegative={false}
                    decimalScale={0}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.EXCHANGES.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.EXCHANGES.label}>
                <TransientField>
                  <CheckboxArray
                    row
                    name={`targetingProfile.${TARGETING_TACTICS.EXCHANGES.key.toLowerCase()}`}
                    options={ACCEPTED_EXCHANGES}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.VIDEO_SKIPPABILITY.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.VIDEO_SKIPPABILITY.label}>
                <TransientField>
                  <RadioGroup
                    name={`targetingProfile.${TARGETING_TACTICS.VIDEO_SKIPPABILITY.key.toLowerCase()}`}
                    options={ACCEPTED_SKIPPABILITY_OPTIONS}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.deal_ids.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.deal_ids.label}>
                <TransientField>
                  <TextField
                    label=""
                    name={`targetingProfile.${TARGETING_TACTICS.deal_ids.key.toLowerCase()}`}
                    multiline
                    maxRows={8}
                    sx={{ width: { xs: '100%' } }}
                    placeholder="exchange:dealid:price"
                    normalizeOutput={normalizeTextFieldOutput}
                    normalizeInput={normalizeTextFieldInput}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.restricted_targeting.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.restricted_targeting.label}>
                <TransientField>
                  <RadioGroup
                    name={`targetingProfile.${TARGETING_TACTICS.restricted_targeting.key.toLowerCase()}`}
                    options={ACCEPTED_RESTRICTED_TARGETINGS}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.VIDEO_AD_POSITION.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.VIDEO_AD_POSITION.label}>
                <TransientField>
                  <CheckboxArray
                    row
                    name={`targetingProfile.${TARGETING_TACTICS.VIDEO_AD_POSITION.key.toLowerCase()}`}
                    options={ACCEPTED_VIDEO_AD_POSITIONS}
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.WHITELISTED_STATE_IDS.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.WHITELISTED_STATE_IDS.label}>
                <TransientField>
                  <AutocompleteArray
                    label=""
                    name={`targetingProfile.${TARGETING_TACTICS.WHITELISTED_STATE_IDS.key.toLowerCase()}`}
                    sx={{ width: '100%' }}
                    options={ACCEPTED_REGION_STATES}
                    autoHighlight
                    normalizeOutput={(outputArray) => outputArray.map((state) => state.value)}
                    normalizeInput={(stateIdArray) =>
                      ACCEPTED_REGION_STATES?.filter((state) => stateIdArray.includes(state.value))
                    }
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.WHITELISTED_NIELSEN_DMAS.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.WHITELISTED_NIELSEN_DMAS.label}>
                <TransientField>
                  <AutocompleteArray
                    label=""
                    name={`targetingProfile.${TARGETING_TACTICS.WHITELISTED_NIELSEN_DMAS.key.toLowerCase()}`}
                    sx={{ width: '100%' }}
                    options={ACCEPTED_REGION_DMAS}
                    autoHighlight
                    normalizeOutput={(outputArray) => outputArray.map((dma) => dma.value)}
                    normalizeInput={(dmaIdArray) =>
                      ACCEPTED_REGION_DMAS?.filter((dma) => dmaIdArray.includes(dma.value))
                    }
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.dv_custom_segment_inclusion.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.dv_custom_segment_inclusion.label}>
                <TransientField>
                  <AutocompleteArray
                    label=""
                    name={`targetingProfile.${TARGETING_TACTICS.dv_custom_segment_inclusion.key.toLowerCase()}`}
                    sx={{ width: '100%' }}
                    options={[]}
                    freeSolo
                    autoHighlight
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.dv_custom_segment_exclusion.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.dv_custom_segment_exclusion.label}>
                <TransientField>
                  <AutocompleteArray
                    label=""
                    name={`targetingProfile.${TARGETING_TACTICS.dv_custom_segment_exclusion.key.toLowerCase()}`}
                    sx={{ width: '100%' }}
                    options={[]}
                    freeSolo
                    autoHighlight
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          if (selectedTacticKey === TARGETING_TACTICS.dv_ivt_prebid_filter.key) {
            return (
              <FieldContainer key={selectedTacticKey} label={TARGETING_TACTICS.dv_ivt_prebid_filter.label}>
                <TransientField>
                  <Switch
                    name={`targetingProfile.${TARGETING_TACTICS.dv_ivt_prebid_filter.key.toLowerCase()}`}
                    label="Enabled"
                  />
                </TransientField>
              </FieldContainer>
            );
          }
          return null;
        })}
      </Stack>
    </CollapsiblePaper>
  );
}

export default TargetingTactics;
